<script setup>
    import Divider from '~/components/website/common/Divider.vue';
    import PhoneInput from '~/components/website/common/PhoneInput.vue';
    import PreferenceSelector from '~/components/website/PreferenceSelector.vue';

    defineProps({
        formData: Object,
        errors: Object,
        isSubmitted: Boolean,
    });

    const emit = defineEmits(['update:field', 'toggleOption', 'submit']);

    const formInputFields = [
        {
            key: 'firstName',
            type: 'text',
            placeholder: 'First name',
            icon: 'user-bordered',
            errorKey: 'firstNameError',
        },
        {
            key: 'lastName',
            type: 'text',
            placeholder: 'Last name',
            icon: 'user-bordered',
            errorKey: 'lastNameError',
        },
        {
            key: 'email',
            type: 'text',
            placeholder: 'Enter your email',
            icon: 'email',
            errorKey: 'emailError',
            helperText:
                'We need your email address so that we can send your booking confirmation and enable you to cancel or amend your appointment online should you wish to.',
        },
    ];

    const preferenceSections = [
        {
            title: 'Communication Preferences',
            description: 'Our team may also follow up with you in relation to your booking via the following channels:',
            options: [
                { key: 'whatsappServiceConsent', label: 'WhatsApp' },
                { key: 'smsServiceConsent', label: 'SMS' },
            ],
            helperText: 'Please untick the box to opt-out if you do not wish to receive any messages.'
        },
        {
            title: 'Marketing Preferences',
            description: 'I would like to receive expert fitness insights, inspiring client stories, and exclusive news updates and promotions from Ultimate Performance.',
            options: [
                { key: 'marketingEmail', label: 'Email' },
                { key: 'marketingWhatsapp', label: 'WhatsApp' },
                { key: 'marketingSms', label: 'SMS' },
            ],
            helperText: 'You can opt out at any time.'
        },
    ];

    const handleInput = (field, event) => {
        let value = event.target.value;
        if (field.key === 'firstName' || field.key === 'lastName') {
            value = value.replace(/[^a-zA-Z\s'-]/g, '').slice(0, 20);
        }
        emit('update:field', field.key, value);
    };

</script>

<template>
    <div class="bg-white rounded-3xl py-8 px-5 md:px-8 md:pb-16">
        <!-- form heading -->
        <div class="flex items-center mb-4">
            <p class="font-semibold text-base">Your Details</p>
        </div>

        <!-- form -->
        <form
            @submit.prevent="emit('submit')"
            class="w-full">
            <!-- Input Grid -->
            <div
                class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-x-5 md:gap-y-6">
                <!-- Normal Input Fields -->
                <div
                    v-for="field in formInputFields"
                    :key="field.key"
                    class="w-full">
                    <!-- Input Wrapper -->
                    <div
                        :class="[
                            'flex items-center gap-3 px-4 py-3 rounded-full border transition-all',
                            isSubmitted && errors[field.errorKey]
                                ? 'border-[#CC3D40] error-box-shadow'
                                : 'border-[#A0A2A4]',
                        ]">

                        <!-- Input -->
                        <input
                            v-model="formData[field.key]"
                            :type="field.type"
                            :placeholder="field.placeholder"
                            @input="handleInput(field, $event)"
                            class="w-full bg-transparent outline-none" />
                    </div>

                    <!-- Error -->
                    <p
                        v-if="isSubmitted && errors[field.errorKey]"
                        class="text-[#CC3D40] text-sm mt-1 err-sub-text">
                        {{ errors[field.errorKey] }}
                    </p>

                    <!-- Helper Text -->
                    <p
                        v-if="field.helperText"
                        class="text-sm text-[#888B8D] mt-1">
                        {{ field.helperText }}
                    </p>
                </div>

                <!-- Phone Input Field -->
                <PhoneInput
                    @change="(data) => emit('update:field', 'phone', data.fullNumber)"
                    v-model="formData.phone"
                    :error="errors.phoneError"
                    :showError="isSubmitted"
                    :helperText="`
                        <span class='text-[#161412]'>IMPORTANT:</span>
                        Please ensure your phone number is correct.
                        You will also receive an SMS reminder before your visit.
                    `"
                />
            </div>

            <!-- Communication & Marketing Preference -->
            <PreferenceSelector
                v-for="section in preferenceSections"
                :key="section.title"
                :title="section.title"
                :description="section.description"
                :options="section.options"
                :formData="formData"
                :helperText="section.helperText"
                @toggle="emit('toggleOption', $event)"
            />

            <Divider />

            <div class="flex flex-col gap-8 md:gap-10">
                <!-- disclaimer -->
                <p class="text-[#888B8D]">
                    By clicking 'Submit' I have read and understood the
                    <a
                        href="https://ultimateperformance.com/privacy-policy"
                        class="legal-links"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        privacy policy
                    </a>
                    and
                    <a
                        href="https://ultimateperformance.com/personal-training-terms-conditions"
                        class="legal-links"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        terms and conditions.
                    </a>
                </p>

                 <!-- submit -->
                <button
                    type="submit"
                    class="form-submit-btn w-full md:w-auto"
                >
                     Confirm your booking
                 </button>

                 <!-- info -->
                 <p class="text-[#888B8D] text-sm text-center">
                    Your consultation is designed to help you understand what’s possible,
                    and whether U.P. is right for you. There’s no obligation to sign up.  
                </p>
             </div>

            <!-- Error Message -->
            <div
                v-if="errors.globalError"
                class="mt-5 text-[#b60013] border border-[#b60013] bg-[#f9ccd0] rounded-[16px] p-4"
                role="alert"
                v-html="errors.globalError"
            />
        </form>
    </div>
</template>

<style scoped>
    .form-heading-icon {
        width: 20px;
        height: 20px;
        margin-right: 8px;
    }

    .error-box-shadow {
        box-shadow: 0 0 0 3px rgba(235, 177, 179, 0.3);
    }

    .legal-links {
        color: #7c2629;
    }

    .legal-links:hover {
        text-decoration: underline;
        text-underline-offset: 3px;
    }

    .form-submit-btn {
        display: flex;
        justify-content: center;
        margin: 0 auto;
        border-radius: 999px;
        background: #161412;
        color: #ffffff;
        padding: 12px 24px;
        font-weight: 600;
        line-height: 30px;
        font-size: 20px;
    }

    .form-submit-btn:hover {
        background: rgb(22 20 18 / 80%);
    }
</style>
